// PoContextBar — the purchase-order context switcher above the finance
// position banner. A native select lists every PO by number; a meta row
// beneath surfaces the selected PO's issued date, total value, line count,
// and a status badge, plus an "N active · M historical" tally derived from PO
// status (open = active, closed = historical). PurchaseOrder carries no
// per-PO supplier — the supplier is always the contract's own — so there is
// no supplier line here. An "Add PO" button sits alongside the selector,
// calling the lifted onAddPo prop. Pure/presentational — selection is lifted
// to FinanceTab, which owns the ?po= URL param. A CopyableIdentifier button
// (value hidden — the select already shows it) lets the selected PO number
// be copied to the clipboard.

import { Plus } from "lucide-react";
import { formatAud } from "../../../utils/financeCalc";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import Button from "../../common/Button";
import CopyableIdentifier from "../../common/CopyableIdentifier";
import type { PurchaseOrderStatus, PurchaseOrderWithDerived } from "../../../types/finance";

interface PoContextBarProps {
  purchaseOrders: PurchaseOrderWithDerived[];
  selectedPoId: string;
  onSelectPo: (poId: string) => void;
  onAddPo: () => void;
}

const STATUS_BADGE: Record<
  PurchaseOrderStatus,
  { className: string; label: string }
> = {
  open: { className: "badge-active", label: "Open" },
  closed: { className: "badge-closed", label: "Closed" },
};

export default function PoContextBar({
  purchaseOrders,
  selectedPoId,
  onSelectPo,
  onAddPo,
}: PoContextBarProps) {
  const selected =
    purchaseOrders.find((po) => po.id === selectedPoId) ?? purchaseOrders[0];
  const activeCount = purchaseOrders.filter((po) => po.status === "open").length;
  const historicalCount = purchaseOrders.length - activeCount;
  const badge = STATUS_BADGE[selected.status];
  const lineCount = selected.lines.length;

  return (
    <div className="rounded-lg border border-slate-200/60 bg-white p-4 shadow-soft-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-soft-sm-dark">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label
            htmlFor="po-selector"
            className="text-caption font-medium text-slate-500 dark:text-slate-400"
          >
            Purchase order
          </label>
          <select
            id="po-selector"
            className="input-sm w-auto min-w-[10rem] font-medium"
            value={selected.id}
            onChange={(e) => onSelectPo(e.target.value)}
          >
            {purchaseOrders.map((po) => (
              <option key={po.id} value={po.id}>
                {po.poNumber}
              </option>
            ))}
          </select>
          <CopyableIdentifier value={selected.poNumber} label="PO number" hideValue />
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={onAddPo}
          >
            Add PO
          </Button>
        </div>
        <span className="text-caption text-slate-500 dark:text-slate-400">
          {activeCount} active · {historicalCount} historical
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-slate-600 dark:text-slate-400">
        {selected.issuedDate && (
          <>
            <span>{formatDayMonthYear(selected.issuedDate)}</span>
            <span aria-hidden="true">·</span>
          </>
        )}
        <span className="tabular-nums text-slate-700 dark:text-slate-300">
          {formatAud(selected.totalValue)}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          {lineCount} {lineCount === 1 ? "line" : "lines"}
        </span>
        <span aria-hidden="true">·</span>
        <span className={badge.className}>{badge.label}</span>
      </div>
    </div>
  );
}
